import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ChevronRight, Clock, Folder, FolderOpen, Hash, Search, Tag, X } from 'lucide-react';
import Modal from '../ui/Modal';
import { api } from '../../services/authService';
import { softWrap } from '../../utils/softWrap';
import { columnLabel } from './criteriaUtils';

const RECENTS_KEY = 'cube.segments.recentColumns';
const RECENTS_MAX = 8;

function readRecents() {
  try {
    const raw = window.localStorage.getItem(RECENTS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

export function rememberRecentColumn(hit) {
  if (!hit?.column) return;
  try {
    const key = [hit.catalog, hit.schema, hit.table, hit.column].join('.');
    const next = [hit, ...readRecents().filter((h) => [h.catalog, h.schema, h.table, h.column].join('.') !== key)].slice(
      0,
      RECENTS_MAX
    );
    window.localStorage.setItem(RECENTS_KEY, JSON.stringify(next));
  } catch (e) {
    /* ignore */
  }
}

function formatApiErrorDetail(detail) {
  if (detail == null) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && e.msg != null ? e.msg : JSON.stringify(e))).join('\n');
  if (typeof detail === 'object') return JSON.stringify(detail);
  return String(detail);
}

function hitKey(h) {
  return [h.catalog, h.schema, h.table, h.column].join('.');
}

function folderOf(h) {
  return h.folder_path || (Array.isArray(h.folder_list) ? h.folder_list.join('|') : '') || '';
}

const valuePreviewCache = new Map();

function useValuePreview(hit) {
  const key = hit?.has_value_list ? hitKey(hit) : '';
  const [values, setValues] = useState(() => (key && valuePreviewCache.get(key)) || null);
  useEffect(() => {
    if (!key) {
      setValues(null);
      return undefined;
    }
    if (valuePreviewCache.has(key)) {
      setValues(valuePreviewCache.get(key));
      return undefined;
    }
    let cancelled = false;
    api
      .get('/api/v2/segments/column-values', {
        params: { catalog: hit.catalog, schema: hit.schema, table: hit.table, column_name: hit.column },
      })
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : [];
        valuePreviewCache.set(key, list);
        if (!cancelled) setValues(list);
      })
      .catch(() => {
        if (!cancelled) setValues([]);
      });
    return () => {
      cancelled = true;
    };
  }, [key, hit]);
  return values;
}

function DetailPanel({ hit }) {
  const values = useValuePreview(hit);
  if (!hit) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center text-sm text-zinc-400">
        <Search className="mb-2 h-6 w-6" aria-hidden />
        Search or browse a folder, then hover a column to see its details.
      </div>
    );
  }
  const folder = folderOf(hit);
  return (
    <div className="flex h-full flex-col gap-3 overflow-y-auto px-5 py-4 text-sm">
      <div>
        <h4 className="font-display text-base font-bold text-[#18181B]">{columnLabel(hit)}</h4>
        {hit.column && hit.column !== columnLabel(hit) ? (
          <p className="mt-0.5 font-mono text-[11px] text-zinc-400">{hit.column}</p>
        ) : null}
      </div>
      {folder ? (
        <p className="flex flex-wrap items-center gap-1 text-xs text-zinc-500">
          <Folder className="h-3.5 w-3.5 shrink-0" aria-hidden />
          {folder.split('|').map((seg, i, arr) => (
            <span key={`${seg}-${i}`} className="inline-flex max-w-full items-center gap-1">
              <span className="whitespace-normal break-words">{softWrap(seg, 16)}</span>
              {i < arr.length - 1 ? <ChevronRight className="h-3 w-3 shrink-0 text-zinc-300" aria-hidden /> : null}
            </span>
          ))}
        </p>
      ) : null}
      <p className="leading-relaxed text-zinc-700">
        {hit.column_description ? hit.column_description : <span className="italic text-zinc-400">No description yet.</span>}
      </p>
      {Array.isArray(hit.column_tags) && hit.column_tags.length ? (
        <div className="flex flex-wrap gap-1">
          {hit.column_tags.slice(0, 12).map((t) => (
            <span key={t} className="inline-flex items-center gap-1 rounded-full bg-[#E8EEF5] px-2 py-0.5 text-[11px] text-zinc-600">
              <Tag className="h-3 w-3" aria-hidden />
              {t}
            </span>
          ))}
        </div>
      ) : null}
      {hit.has_value_list ? (
        <div>
          <p className="mb-1 text-[11px] font-semibold uppercase tracking-wide text-zinc-400">Possible values</p>
          {values == null ? (
            <p className="text-xs text-zinc-400">Loading…</p>
          ) : values.length === 0 ? (
            <p className="text-xs text-zinc-400">No value list available.</p>
          ) : (
            <div className="flex flex-wrap gap-1">
              {values.slice(0, 24).map((v) => (
                <span key={v} className="rounded border border-hairline bg-surface px-1.5 py-0.5 text-[11px] text-zinc-700">
                  {v}
                </span>
              ))}
              {values.length > 24 ? <span className="text-[11px] text-zinc-400">+{values.length - 24} more</span> : null}
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Command-palette style column picker. One search box over the ES column catalog,
 * a folder rail for browsing, rich result rows, and a detail panel.
 *
 * `onSelect(hit)` receives the raw catalog hit (catalog/schema/table/column/column_type/
 * has_value_list/display_name/folder_list/column_description/column_tags).
 */
const ColumnPicker = ({ isOpen, onClose, onSelect, initialQuery = '' }) => {
  const [query, setQuery] = useState(initialQuery);
  const [folder, setFolder] = useState('');
  const [hits, setHits] = useState([]);
  const [folders, setFolders] = useState([]);
  const [allFolders, setAllFolders] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [active, setActive] = useState(0);
  const [recents, setRecents] = useState([]);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const timerRef = useRef(null);
  const reqRef = useRef(0);
  const allFoldersRef = useRef(false);

  useEffect(() => {
    if (!isOpen) return;
    setQuery(initialQuery || '');
    setFolder('');
    setActive(0);
    setRecents(readRecents());
    const t = setTimeout(() => inputRef.current?.focus(), 30);
    return () => clearTimeout(t);
  }, [isOpen, initialQuery]);

  // Facets are scoped to the selected folder, so the rail needs an unfiltered copy of the
  // tree to keep the rest of the catalog navigable. The root search usually supplies it;
  // this covers opening straight into a query. `size: 1` because only the aggs are needed.
  useEffect(() => {
    if (!isOpen || !folder || allFoldersRef.current) return undefined;
    allFoldersRef.current = true;
    let cancelled = false;
    api
      .get('/api/v2/segments/column-search', { params: { q: '', size: 1 } })
      .then((res) => {
        if (!cancelled) setAllFolders(Array.isArray(res.data?.folders) ? res.data.folders : []);
      })
      .catch(() => {
        allFoldersRef.current = false;
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, folder]);

  const runSearch = useCallback((q, folderPath) => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const id = ++reqRef.current;
      setLoading(true);
      try {
        const params = { q: q.trim(), size: 200 };
        if (folderPath) params.folder_path = folderPath.split('|').join(',');
        const res = await api.get('/api/v2/segments/column-search', { params });
        if (id !== reqRef.current) return;
        const nextFolders = Array.isArray(res.data?.folders) ? res.data.folders : [];
        setHits(Array.isArray(res.data?.hits) ? res.data.hits : []);
        setFolders(nextFolders);
        if (!folderPath && !q.trim()) {
          setAllFolders(nextFolders);
          allFoldersRef.current = true;
        }
        setTotal(Number(res.data?.total) || 0);
        setError(res.data?.error || '');
        setActive(0);
      } catch (e) {
        if (id !== reqRef.current) return;
        setHits([]);
        setError(formatApiErrorDetail(e.response?.data?.detail) || 'Column search failed');
      } finally {
        if (id === reqRef.current) setLoading(false);
      }
    }, q ? 160 : 0);
  }, []);

  useEffect(() => {
    if (!isOpen) return undefined;
    runSearch(query, folder);
    return () => clearTimeout(timerRef.current);
  }, [isOpen, query, folder, runSearch]);

  // Rail tree: the current folder's children come from the (folder-scoped) facets, while
  // everything outside it — top-level folders and the ancestors' siblings — falls back to
  // the unfiltered tree so browsing a subfolder never hides the rest of the catalog.
  const railFolders = useMemo(() => {
    const byPath = new Map();
    for (const f of folders) {
      if (f?.path) byPath.set(f.path, { path: f.path, count: f.count });
    }
    if (folder) {
      for (const f of allFolders) {
        if (!f?.path || byPath.has(f.path)) continue;
        if (f.path === folder || f.path.startsWith(`${folder}|`)) continue;
        byPath.set(f.path, { path: f.path, count: f.count });
      }
    }
    const childrenOf = (prefix) => {
      const depth = prefix ? prefix.split('|').length : 0;
      const out = [];
      for (const entry of byPath.values()) {
        const parts = entry.path.split('|');
        if (parts.length !== depth + 1) continue;
        if (prefix && !entry.path.startsWith(`${prefix}|`)) continue;
        out.push({ ...entry, name: parts[parts.length - 1], depth });
      }
      return out.sort((a, b) => a.name.localeCompare(b.name));
    };
    const rows = [];
    const walk = (prefix) => {
      for (const entry of childrenOf(prefix)) {
        const onPath = folder === entry.path || folder.startsWith(`${entry.path}|`);
        rows.push({ ...entry, onPath, isCurrent: folder === entry.path });
        if (onPath) walk(entry.path);
      }
    };
    walk('');
    return rows;
  }, [folders, allFolders, folder]);

  const showRecents = !query.trim() && !folder && recents.length > 0;

  const rows = useMemo(() => {
    const out = [];
    if (showRecents) {
      out.push({ type: 'header', label: 'Recently used', icon: Clock, key: 'h-recent' });
      recents.forEach((h) => out.push({ type: 'hit', hit: h, key: `r-${hitKey(h)}` }));
    }
    let lastFolder = null;
    const sorted = query.trim() ? hits : [...hits].sort((a, b) => folderOf(a).localeCompare(folderOf(b)) || columnLabel(a).localeCompare(columnLabel(b)));
    if (showRecents && sorted.length) out.push({ type: 'header', label: 'All columns', icon: Hash, key: 'h-all' });
    sorted.forEach((h) => {
      const f = folderOf(h);
      if (!query.trim() && f !== lastFolder) {
        out.push({ type: 'subheader', label: f ? f.split('|').join(' › ') : 'Uncategorized', key: `f-${f || 'none'}` });
        lastFolder = f;
      }
      out.push({ type: 'hit', hit: h, key: `h-${hitKey(h)}` });
    });
    return out;
  }, [hits, recents, showRecents, query]);

  const hitRows = useMemo(() => rows.filter((r) => r.type === 'hit'), [rows]);
  const activeHit = hitRows[active]?.hit || null;

  useEffect(() => {
    if (!listRef.current) return;
    const el = listRef.current.querySelector(`[data-hit-index="${active}"]`);
    if (el) el.scrollIntoView({ block: 'nearest' });
  }, [active]);

  const pick = (hit) => {
    if (!hit) return;
    rememberRecentColumn(hit);
    onSelect(hit);
    onClose();
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, Math.max(hitRows.length - 1, 0)));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      pick(activeHit);
    } else if (e.key === 'Backspace' && !query && folder) {
      e.preventDefault();
      setFolder(folder.split('|').slice(0, -1).join('|'));
    }
  };

  const crumbs = folder ? folder.split('|') : [];

  return (
    <Modal isOpen={isOpen} onClose={onClose} className="w-full max-w-5xl h-[min(640px,88vh)] flex flex-col overflow-hidden !p-0">
      <div className="flex flex-wrap items-center gap-2 border-b border-hairline px-4 py-3 pr-12">
        <Search className="h-4 w-4 shrink-0 text-zinc-400" aria-hidden />
        {crumbs.map((c, i) => (
          <button
            key={`${c}-${i}`}
            type="button"
            onClick={() => setFolder(crumbs.slice(0, i + 1).join('|'))}
            className="inline-flex max-w-[40%] items-center gap-1 rounded bg-[#E8EEF5] px-2 py-0.5 text-left text-xs font-medium text-zinc-700 hover:bg-[#e2e8f0]"
          >
            <span className="min-w-0 whitespace-normal break-words">{softWrap(c, 16)}</span>
            {i === crumbs.length - 1 ? (
              <X
                className="h-3 w-3 text-zinc-400"
                aria-label="Leave folder"
                onClick={(e) => {
                  e.stopPropagation();
                  setFolder(crumbs.slice(0, -1).join('|'));
                }}
              />
            ) : null}
          </button>
        ))}
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder={folder ? `Search in ${crumbs[crumbs.length - 1]}…` : 'Search columns by name, description, or tag…'}
          className="h-9 min-w-0 flex-1 bg-transparent text-sm text-[#18181B] placeholder:text-zinc-400 focus:outline-none"
          aria-label="Search columns"
          autoComplete="off"
        />
        {loading ? <span className="text-xs text-zinc-400">Searching…</span> : <span className="text-xs text-zinc-400">{total.toLocaleString()} columns</span>}
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-[200px_minmax(0,1fr)_300px] max-md:grid-cols-1">
        {/* Folder rail */}
        <nav className="hidden min-h-0 overflow-y-auto border-r border-hairline bg-[#F8FAFC] p-2 md:block" aria-label="Folders">
          <button
            type="button"
            onClick={() => setFolder('')}
            className={`mb-1 flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm ${!folder ? 'bg-white font-medium text-[#18181B] shadow-sm' : 'text-zinc-600 hover:bg-white/70'}`}
          >
            <Hash className="h-4 w-4 text-zinc-400" aria-hidden />
            All columns
          </button>
          {railFolders.length ? (
            <p className="mb-1 mt-3 px-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
              Folders
            </p>
          ) : null}
          {railFolders.map((f) => {
            const Icon = f.onPath ? FolderOpen : Folder;
            return (
              <button
                key={f.path}
                type="button"
                aria-current={f.isCurrent ? 'true' : undefined}
                onClick={() => {
                  setFolder(f.path);
                  inputRef.current?.focus();
                }}
                style={{ paddingLeft: `${8 + Math.min(f.depth, 4) * 12}px` }}
                className={`flex w-full items-start gap-2 rounded-md py-1.5 pr-2 text-left text-sm ${
                  f.isCurrent ? 'bg-white font-medium text-[#18181B] shadow-sm' : 'text-zinc-700 hover:bg-white/70'
                }`}
              >
                <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${f.isCurrent ? 'text-zinc-500' : 'text-zinc-400'}`} aria-hidden />
                <span className="min-w-0 flex-1 whitespace-normal break-words">{softWrap(f.name, Math.max(16 - f.depth * 2, 8))}</span>
                <span className="shrink-0 text-[11px] tabular-nums text-zinc-400">{f.count}</span>
              </button>
            );
          })}
        </nav>

        {/* Results */}
        <div ref={listRef} className="min-h-0 overflow-y-auto py-1" role="listbox" aria-label="Columns">
          {!error && !loading && total > hits.length ? (
            <p className="px-4 pt-2 text-[11px] text-zinc-400">
              Showing the first {hits.length.toLocaleString()} of {total.toLocaleString()} — type to narrow, or pick a folder.
            </p>
          ) : null}
          {error ? (
            <p className="px-4 py-6 text-sm text-red-600">{error}</p>
          ) : !loading && hitRows.length === 0 ? (
            <p className="px-4 py-6 text-sm text-zinc-500">No columns match “{query}”. Try a broader word, a tag, or browse folders.</p>
          ) : null}
          {(() => {
            let idx = -1;
            return rows.map((row) => {
              if (row.type === 'header') {
                const Icon = row.icon;
                return (
                  <p key={row.key} className="mt-2 flex items-center gap-1.5 px-4 pb-1 pt-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
                    <Icon className="h-3 w-3" aria-hidden />
                    {row.label}
                  </p>
                );
              }
              if (row.type === 'subheader') {
                return (
                  <p key={row.key} className="mt-2 flex items-start gap-1.5 px-4 pb-1 text-[11px] font-medium text-zinc-500">
                    <FolderOpen className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
                    <span className="min-w-0 whitespace-normal break-words">{softWrap(row.label, 20)}</span>
                  </p>
                );
              }
              idx += 1;
              const i = idx;
              const h = row.hit;
              const isActive = i === active;
              return (
                <button
                  key={row.key}
                  type="button"
                  role="option"
                  aria-selected={isActive}
                  data-hit-index={i}
                  onMouseEnter={() => setActive(i)}
                  onClick={() => pick(h)}
                  className={`flex w-full items-start gap-3 px-4 py-2 text-left ${isActive ? 'bg-[#E8EEF5]' : 'hover:bg-[#F4F7FB]'}`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate text-sm font-medium text-[#18181B]">{columnLabel(h)}</span>
                    </div>
                    {h.column_description ? (
                      <p className="truncate text-xs text-zinc-500">{h.column_description}</p>
                    ) : null}
                  </div>
                  {query.trim() && folderOf(h) ? (
                    <span className="hidden max-w-[40%] shrink-0 whitespace-normal break-words pt-0.5 text-right text-[11px] text-zinc-400 lg:block">
                      {softWrap(folderOf(h).split('|').join(' › '), 16)}
                    </span>
                  ) : null}
                </button>
              );
            });
          })()}
        </div>

        {/* Detail */}
        <aside className="hidden min-h-0 border-l border-hairline bg-[#F8FAFC] md:block">
          <DetailPanel hit={activeHit} />
        </aside>
      </div>

      <div className="flex items-center justify-between border-t border-hairline px-4 py-2 text-[11px] text-zinc-400">
        <span>
          <kbd className="rounded border border-hairline bg-white px-1">↑</kbd> <kbd className="rounded border border-hairline bg-white px-1">↓</kbd> to move ·{' '}
          <kbd className="rounded border border-hairline bg-white px-1">Enter</kbd> to choose · <kbd className="rounded border border-hairline bg-white px-1">Esc</kbd> to close
        </span>
        <span>Only columns marked visible in the data dictionary are shown.</span>
      </div>
    </Modal>
  );
};

export default ColumnPicker;
